import { useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Loader2, PencilLine, Network, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { US_STATES } from "../lib/constants";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { WhereToNext } from "./WhereToNext";

const FIELDS = [
  ["name", "Business name"], ["category", "Category"], ["address", "Street address"], ["city", "City"], ["state", "State (e.g. FL)"], ["zip", "ZIP"],
  ["phone", "Business phone"], ["email", "Business email"], ["website", "Website"], ["current_pos", "Current POS"],
  ["contact_name", "Owner / manager"], ["contact_title", "Title"], ["contact_phone", "Owner phone"], ["contact_email", "Owner email"],
  ["source_url", "Where we found it (link)"], ["signal_quote", "About / what they said"],
];
const SECTIONS = { future: "Future Leads", other: "Around Me", potential: "Potential", signed: "Signed", archived: "Dead deals" };
const input = "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm";

// Jeff only: find a business by name (AI fills in the details and owner), check it, then send it Where to Next.
export const AddLeadButton = ({ myId, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState({ name: "", city: "", state: "" });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sisters, setSisters] = useState(null);
  const [added, setAdded] = useState([]);
  const reset = () => { setOpen(false); setForm(null); setSisters(null); setAdded([]); setAllReps(null); setQ({ name: "", city: "", state: "" }); };
  const [looking, setLooking] = useState(null);
  const [allReps, setAllReps] = useState(null);
  const pickAll = async () => {
    try { setAllReps((await api.get("/users/names")).data.filter((u) => u.id !== myId)); } catch (e) { toast.error(formatApiError(e)); }
  };
  const addAll = async (u) => {
    const todo = sisters.filter((x) => !x.in_app && !added.includes(x.name));
    try {
      await api.post("/leads/sisters/add-all", { sisters: todo.map((x) => ({ name: x.name, city: x.city || null, state: x.state })), for_uid: u?.id || null });
      setAdded([...added, ...todo.map((x) => x.name)]);
      setAllReps(null);
      toast.success(`Adding ${todo.length} sister ${todo.length === 1 ? "company" : "companies"} to ${u ? `${u.name}'s` : "your"} Potential. They'll show up over the next few minutes.`);
      setTimeout(() => onAdded?.(), 90000);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const lookup = async (target = q) => {
    setBusy(true);
    setLooking(target.name);
    try {
      const { data } = await api.post("/leads/lookup", { name: target.name, city: target.city || null, state: target.state || null });
      setQ(target);
      setForm({ category: "restaurants", ...target, ...data });
      document.querySelector('[data-testid="add-lead-dialog"]')?.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Found it. Check the details, then pick Where to Next.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); setLooking(null); }
  };
  const save = async (m) => {
    if (!form.name?.trim() || !US_STATES[(form.state || "").toUpperCase()]) return toast.error("Add the business name and a 2-letter state");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...form, state: form.state.toUpperCase(), bucket: m.bucket, reason: m.reason, note: m.note, for_uid: m.for_uid || null, jeff_note: m.jeff_note || null });
      toast.success(`${form.name} ${data.created ? "added" : "updated"} in ${m.forName ? `${m.forName}'s ` : ""}${SECTIONS[m.bucket] || "All leads"}`);
      onAdded?.();
      if (sisters?.length) { setAdded([...added, form.name]); setForm(null); } else reset();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const findSisters = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads/sisters", { name: form.name, city: form.city || null, state: form.state || null, website: form.website || null, contact_name: form.contact_name || null });
      setSisters(data);
      if (!data.length) toast.message("No sister restaurants found for this owner");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const sisterList = sisters?.length > 0 && (
    <div className="rounded-2xl border border-slate-200 p-3 space-y-2" data-testid="add-lead-sisters">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-slate-900">Same owner: {sisters[0].group || "sister restaurants"}</p>
        {sisters.some((x) => !x.in_app && !added.includes(x.name)) && (
          <button onClick={pickAll} disabled={busy} data-testid="add-lead-sisters-all"
            className="h-9 px-3 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 text-xs font-bold disabled:opacity-50 transition-colors">Add all to Potential</button>
        )}
      </div>
      {allReps && (
        <div className="rounded-xl bg-slate-50 p-2 space-y-1.5" data-testid="add-lead-sisters-reps">
          <p className="text-xs text-slate-500">Whose Potential gets all of them?</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={() => addAll(null)} className="h-9 rounded-full border border-slate-200 bg-white text-xs font-semibold hover:border-blue-500" data-testid="add-lead-sisters-mine">My Potential</button>
            {allReps.map((u) => (
              <button key={u.id} onClick={() => addAll(u)} className="h-9 rounded-full border border-slate-200 bg-white text-xs font-semibold hover:border-blue-500" data-testid={`add-lead-sisters-rep-${u.id}`}>{u.name}</button>
            ))}
          </div>
          <button onClick={() => setAllReps(null)} className="text-xs font-semibold text-slate-500" data-testid="add-lead-sisters-cancel">Cancel</button>
        </div>
      )}
      {sisters.map((x, i) => {
        const done = x.in_app || added.includes(x.name);
        return (
          <div key={x.name} className="flex items-center justify-between gap-3 border-t border-slate-100 pt-2 first-of-type:border-0" data-testid={`add-lead-sister-${i}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{x.name}</p>
              <p className="text-xs text-slate-500 truncate">{[x.city, x.state].filter(Boolean).join(", ")}{x.why ? ` · ${x.why}` : ""}</p>
            </div>
            {done ? <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1 shrink-0"><Check className="h-3.5 w-3.5" />{added.includes(x.name) ? "Added" : "In the app"}</span> : (
              <button onClick={() => lookup({ name: x.name, city: x.city || "", state: x.state })} disabled={busy} data-testid={`add-lead-sister-add-${i}`}
                className="h-9 px-3 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold shrink-0 disabled:opacity-50 flex items-center gap-1.5 transition-colors">{looking === x.name ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Adding…</> : "Add company"}</button>
            )}
          </div>
        );
      })}
    </div>
  );
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="add-lead-button"
        className="h-10 px-4 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors shrink-0"><Plus className="h-4 w-4" />Add lead</button>
      <Dialog open={open} onOpenChange={(o) => !o && !busy && reset()}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="add-lead-dialog">
          <DialogTitle className="font-display text-xl font-extrabold">Add a lead</DialogTitle>
          <DialogDescription>Type the business and town. We'll look up the address, phone, website, POS and owner for you.</DialogDescription>
          {!form ? (
            <div className="space-y-3">
              <input value={q.name} onChange={(e) => setQ({ ...q, name: e.target.value })} placeholder="Business name, e.g. McGuire's Irish Pub" className={input} data-testid="add-lead-name" />
              <div className="grid grid-cols-3 gap-2">
                <input value={q.city} onChange={(e) => setQ({ ...q, city: e.target.value })} placeholder="City" className={`${input} col-span-2`} data-testid="add-lead-city" />
                <input value={q.state} onChange={(e) => setQ({ ...q, state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="State" className={input} data-testid="add-lead-state" />
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => lookup()} disabled={busy || q.name.trim().length < 2} data-testid="add-lead-lookup"
                  className="h-11 px-5 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50 transition-colors">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}{busy ? "Adding company… (up to a minute)" : "Add company"}</button>
                <button onClick={() => setForm({ category: "restaurants", ...q })} disabled={busy} data-testid="add-lead-manual"
                  className="h-11 px-5 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:border-blue-500 flex items-center gap-2 transition-colors"><PencilLine className="h-4 w-4" />Fill in by hand</button>
              </div>
              {sisterList}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-2" data-testid="add-lead-form">
                {FIELDS.map(([k, label]) => (
                  <label key={k} className={k === "signal_quote" || k === "source_url" ? "sm:col-span-2" : ""}>
                    <span className="text-[11px] font-semibold text-slate-500">{label}</span>
                    {k === "signal_quote"
                      ? <textarea value={form[k] || ""} onChange={set(k)} rows={2} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`add-lead-field-${k}`} />
                      : <input value={form[k] || ""} onChange={set(k)} className={input} data-testid={`add-lead-field-${k}`} />}
                  </label>
                ))}
              </div>
              {sisters === null ? (
                <button onClick={findSisters} disabled={busy || !form.name} data-testid="add-lead-find-sisters"
                  className="h-10 px-4 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:border-blue-500 flex items-center gap-2 disabled:opacity-50 transition-colors">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Network className="h-4 w-4" />}{busy ? "Searching… (up to a minute)" : "Find sister restaurants (same owner)"}</button>
              ) : sisterList}
              <WhereToNext lead={{ id: form.name || "new" }} leadState={form.state} current="" isAdmin myId={myId} testid="add-lead-wtn" onGo={save} />
              <button onClick={() => setForm(null)} disabled={busy} className="text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="add-lead-start-over">Start over</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
